/* ==========================================================================
   PDP Reviews Preview
   ==========================================================================

   Keeps the module's review count and rating identical to the review app's own
   numbers, instead of letting the two disagree on the same page.

   snippets/c-pdp-reviews-preview.liquid server-renders the count from the product's
   `reviews` metafields, falling back to the theme setting when a product has none.
   Judge.me does not necessarily sync those metafields, so that number can differ from
   the one its preview badge shows above the title. This reads the badge's own
   `data-number-of-reviews` / `data-average-rating` and writes them into the module, so
   whatever the app reports is what the buy zone shows.

   The copy is never touched - only the numbers inside `[data-c-reviews-count]`,
   `[data-c-reviews-rating]` and the `{count}` placeholder of the link template are
   substituted, so the heading, count label and link wording stay theme settings.

   If the badge is absent (swimwear/shapewear templates have it disabled, or the app
   has not loaded) the server-rendered value simply stays as it is.

   Also smooth-scrolls the "Bekijk alle N beoordelingen" link down to the full reviews
   section, clearing the sticky header, instead of the browser's instant jump. The handler
   is delegated on the module and matched on [data-c-reviews-scroll], so that link is the
   only one it touches - every other anchor on the page behaves exactly as before.
   ========================================================================== */

(function () {
  'use strict';

  var MODULE_SELECTOR = '[data-c-reviews]';

  /* the Judge.me preview badge carries the numbers as data attributes */
  var BADGE_SELECTOR = '[data-number-of-reviews]';
  var COUNT_ATTRIBUTE = 'data-number-of-reviews';
  var RATING_ATTRIBUTE = 'data-average-rating';

  var applied = null;
  var observer = null;
  var scrollBound = false;

  function activeModule() {
    return document.querySelector(MODULE_SELECTOR);
  }

  /* Judge.me also badges the product cards further down the page, so match the badge to
     this product by id and only fall back to the one inside the buy zone. */
  function badgeFor(module) {
    var productId = module.getAttribute('data-c-reviews-product');
    var badges = document.querySelectorAll(BADGE_SELECTOR);
    var fallback = null;

    for (var i = 0; i < badges.length; i++) {
      var scope = badges[i].closest('[data-id]');

      if (productId && scope && scope.getAttribute('data-id') === productId) return badges[i];
      if (!fallback && badges[i].closest('.product-info')) fallback = badges[i];
    }

    return fallback;
  }

  function formatRating(module, raw) {
    var value = parseFloat(raw);
    if (!isFinite(value) || value <= 0) return null;

    /* one decimal, matching what Liquid renders; the separator is a theme setting */
    var separator = module.getAttribute('data-c-reviews-separator') || '.';
    return value.toFixed(1).replace('.', separator);
  }

  function render(module, count, rating) {
    var countElement = module.querySelector('[data-c-reviews-count]');
    if (countElement) countElement.textContent = count;

    var ratingElement = module.querySelector('[data-c-reviews-rating]');
    var ratingText = formatRating(module, rating);
    if (ratingElement && ratingText) ratingElement.textContent = ratingText;

    /* the link reads "Bekijk alle {count} beoordelingen" - only the number is swapped,
       the wording stays the theme setting */
    var link = module.querySelector('[data-c-reviews-cta]');
    if (link) {
      var template = link.getAttribute('data-c-reviews-cta');
      if (template) link.textContent = template.split('{count}').join(count);
    }
  }

  /* the theme only marks the header sticky when that setting is on, so an offset is
     subtracted only when something is actually pinned over the target. Measured from the
     header's bottom edge, which stays right on mobile too: it covers a sticky announcement
     bar sitting above it, and reads 0 when a hide-on-scroll header is off screen. */
  function headerOffset() {
    var header = document.querySelector('store-header[sticky]');
    if (!header) return 0;

    var bottom = header.getBoundingClientRect().bottom;
    return bottom > 0 ? bottom : 0;
  }

  function bindScroll(module) {
    if (scrollBound) return;
    scrollBound = true;

    module.addEventListener('click', function (event) {
      var link = event.target.closest('[data-c-reviews-scroll]');
      if (!link) return;

      var id = (link.getAttribute('href') || '').split('#')[1];
      var target = id ? document.getElementById(id) : null;

      /* nothing to scroll to on this page - let the browser follow the href as usual */
      if (!target) return;

      event.preventDefault();

      var top = target.getBoundingClientRect().top + window.scrollY - headerOffset();

      try {
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      } catch (error) {
        target.scrollIntoView();
      }
    });
  }

  function sync() {
    var module = activeModule();
    if (!module) return false;

    var badge = badgeFor(module);
    if (!badge) return false;

    var count = (badge.getAttribute(COUNT_ATTRIBUTE) || '').trim();
    var rating = (badge.getAttribute(RATING_ATTRIBUTE) || '').trim();

    /* a product the app reports as having no reviews keeps the server-rendered fallback -
       "0 beoordelingen" would contradict the testimonials the module shows */
    if (!/^\d+$/.test(count) || count === '0') return false;

    var signature = count + '|' + rating;
    if (signature === applied) return true;

    applied = signature;
    render(module, count, rating);
    return true;
  }

  function start() {
    var module = activeModule();
    if (module) bindScroll(module);

    if (sync()) {
      if (observer) observer.disconnect();
      return;
    }

    /* the badge is rendered by the app and can arrive after this script runs */
    if (observer) return;

    var pending = null;
    observer = new MutationObserver(function () {
      if (pending) return;
      pending = window.requestAnimationFrame(function () {
        pending = null;
        if (sync()) observer.disconnect();
      });
    });

    observer.observe(document.body, {
      attributeFilter: [COUNT_ATTRIBUTE, RATING_ATTRIBUTE],
      attributes: true,
      childList: true,
      subtree: true
    });
  }

  document.addEventListener('DOMContentLoaded', start);
  window.addEventListener('ig:ready', start);
  if (document.readyState !== 'loading') start();
})();
